import Combobox from "@/components/Combobox";
import InputError from "@/components/InputError";
import Modal from "@/components/Modal";
import SkeletonRow from "@/components/SkeletonRow";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import useSessionStorage from "@/hooks/useSessionStorage";
import api from "@/lib/axios";
import { formatRupiah } from "@/lib/utils";
import {
  getDataKeluargaTambahan,
  getListDataKeluarga,
  getListHubunganKeluarga,
  getListJumlahTanggungan,
} from "@/services/getData";
import { DataKeluarga, HubunganKeluarga, JumlahTanggungan } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AlertCircle,
  Briefcase,
  ChevronRight,
  Edit,
  FileText,
  Info,
  Loader2,
  Plus,
  Trash2,
  Users,
  Wallet,
} from "lucide-react"; // Gunakan Lucide icons
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaCheck } from "react-icons/fa6";
import Form from "./Form";

const patchDataKeluargaTambahan = async (form: {
  id_data_pendaftaran: string;
  jumlah_tanggungan_orangtua_wali: string;
  biaya_hidup_keluarga_perbulan: string;
  is_ada_sarjana_keluarga?: string | number | boolean;
  nama_sarjana_keluarga?: string;
}) => {
  const response = await api.patch(
    "/siswa/daftar-kembali/data-keluarga/update/data-keluarga-tambahan",
    form,
    { withCredentials: true },
  );
  return response.data;
};

export default function Keluarga({
  idDataPendaftaran,
  isValidPermanen,
}: {
  idDataPendaftaran: string;
  isValidPermanen: boolean;
}) {
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showInsert, setShowInsert] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [selectedRow, setSelectedRow] = useState<DataKeluarga | null>(null);

  const [openJumlahTanggungan, setOpenJumlahTanggungan] = useState(false);
  const [openSarjana, setOpenSarjana] = useState(false);

  const [form, setForm] = useState({
    id_data_pendaftaran: idDataPendaftaran,
    jumlah_tanggungan_orangtua_wali: "",
    biaya_hidup_keluarga_perbulan: "",
    is_ada_sarjana_keluarga: "",
    nama_sarjana_keluarga: "",
  });

  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: patchDataKeluargaTambahan,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listDataKeluarga"] });
      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      toast.success(data?.message || "Data berhasil disimpan.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "Terjadi kesalahan yang tidak terduga.";
      toast.error(errorMessage);
    },
  });

  // --- QUERY HOOKS (Sama seperti sebelumnya) ---
  const { data: dataHubunganKeluarga } = useQuery({
    queryKey: ["listHubunganKeluarga"],
    queryFn: () => getListHubunganKeluarga(1),
  });
  const dataHubunganKeluargaWajib: HubunganKeluarga[] =
    dataHubunganKeluarga?.filter(
      (item: HubunganKeluarga) => Number(item.is_wajib) === 1,
    );

  const { data: dataJumlahTanggungan } = useQuery({
    queryKey: ["listJumlahTanggungan"],
    queryFn: getListJumlahTanggungan,
    select: (data) =>
      data?.map((dataJumlahTanggungan: JumlahTanggungan) => ({
        label: `${dataJumlahTanggungan.jumlah_tanggungan_text}`,
        value: `${dataJumlahTanggungan.id_ref_jumlah_tanggungan}`,
      })),
  });

  const { data, isPending: isPendingListDataKeluarga } = useQuery({
    queryKey: ["listDataKeluarga", sessionPeriode?.id_sys_periode],
    queryFn: () => getListDataKeluarga(sessionPeriode?.id_sys_periode || ""),
  });

  const {
    data: dataKeluargaTambahan,
    isPending: isPendingDataKeluargaTambahan,
  } = useQuery({
    queryKey: ["dataKeluargaTambahan"],
    queryFn: () => getDataKeluargaTambahan(idDataPendaftaran),
  });

  useEffect(() => {
    if (!dataKeluargaTambahan) return;

    setForm((prev) => ({
      ...prev,
      biaya_hidup_keluarga_perbulan:
        dataKeluargaTambahan?.biaya_hidup_keluarga_perbulan || "",
      jumlah_tanggungan_orangtua_wali:
        dataKeluargaTambahan?.jumlah_tanggungan_orangtua_wali || "",
      is_ada_sarjana_keluarga:
        dataKeluargaTambahan?.is_ada_sarjana_keluarga !== null &&
        dataKeluargaTambahan?.is_ada_sarjana_keluarga !== undefined
          ? String(dataKeluargaTambahan?.is_ada_sarjana_keluarga)
          : "",
      nama_sarjana_keluarga: dataKeluargaTambahan?.nama_sarjana_keluarga || "",
    }));
  }, [dataKeluargaTambahan]);

  const handleSumbitDataTambahan = async () => {
    mutate({
      ...form,
      id_data_pendaftaran: idDataPendaftaran,
      is_ada_sarjana_keluarga: Number(form.is_ada_sarjana_keluarga),
    });
  };

  const isDataKeluargaWajibFilled = dataHubunganKeluargaWajib?.every(
    (hubunganWajib: HubunganKeluarga) =>
      data?.some(
        (dataKeluarga: DataKeluarga) =>
          dataKeluarga.hubungan_keluarga?.id_ref_hubungan_keluarga ===
          hubunganWajib?.id_ref_hubungan_keluarga,
      ),
  );

  const isDataTambahanFilled =
    dataKeluargaTambahan && Object.keys(dataKeluargaTambahan).length > 0;

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* --- HEADER --- */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          Data Keluarga
        </h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
          Lengkapi data keluarga inti dan informasi ekonomi pendukung.
        </p>
      </div>

      {/* --- ALERT INFO --- */}
      <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 text-amber-900 dark:text-amber-200">
        <Info className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        <AlertTitle className="font-bold text-amber-800 dark:text-amber-300">
          Petunjuk Pengisian
        </AlertTitle>
        <AlertDescription className="text-sm mt-2 space-y-2">
          <p>
            Data ini akan divalidasi dengan Kartu Keluarga (KK). Pastikan data
            sesuai.
          </p>
          <ul className="list-disc list-inside space-y-1 ml-1 text-amber-800/80 dark:text-amber-200/80">
            <li>
              Wajib mengisi data <span className="font-semibold">Ayah</span> dan{" "}
              <span className="font-semibold">Ibu</span> (meskipun telah
              meninggal).
            </li>
            <li>
              Jika kedua orang tua meninggal, isikan data{" "}
              <span className="font-semibold">Wali</span> yang membiayai Anda.
            </li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* --- SECTION 1: ANGGOTA KELUARGA --- */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-200 dark:border-slate-800 pb-4">
          <div className="flex items-center gap-3">
            <div
              className={`h-8 w-8 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
                isDataKeluargaWajibFilled
                  ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"
                  : "bg-slate-100 text-slate-500 dark:bg-slate-800"
              }`}
            >
              {isDataKeluargaWajibFilled ? <FaCheck /> : "1"}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                Anggota Keluarga Inti
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Wajib isi:{" "}
                <span className="text-red-500 font-medium">
                  {dataHubunganKeluargaWajib
                    ?.map((item) => item.hubungan_keluarga)
                    .join(", ")}
                </span>
              </p>
            </div>
          </div>

          {!isValidPermanen && (
            <Button onClick={() => setShowInsert(true)} size="sm" className="">
              <Plus className="w-4 h-4 mr-2" /> Tambah Anggota
            </Button>
          )}
        </div>

        {isPendingListDataKeluarga ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <SkeletonRow key={i} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
            <div className="overflow-x-auto w-full">
              <table className="w-full text-sm text-left whitespace-nowrap">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-950 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                  <tr>
                    <th className="px-4 py-3 w-10">#</th>
                    <th className="px-4 py-3">Hubungan</th>
                    <th className="px-4 py-3">Nama Lengkap</th>
                    <th className="px-4 py-3">Pekerjaan</th>
                    <th className="px-4 py-3">Penghasilan</th>
                    <th className="px-4 py-3 text-center">Berkas</th>
                    <th className="px-4 py-3 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {data?.length > 0 ? (
                    data.map((row: DataKeluarga, idx: number) => (
                      <tr
                        key={row.id_data_keluarga}
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                      >
                        {/* ... Isi baris tabel ... */}
                        {/* (Kode isi tabel sama seperti sebelumnya) */}
                        <td className="px-4 py-3 text-center text-slate-400">
                          {idx + 1}
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            variant="outline"
                            className="capitalize bg-slate-50 dark:bg-slate-800"
                          >
                            {row.hubungan_keluarga?.hubungan_keluarga}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-200">
                          {row.nama}
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                          <div className="flex items-center gap-2">
                            <Briefcase className="w-3 h-3 text-slate-400" />
                            {row.pekerjaan?.pekerjaan}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400 font-mono">
                          {row.penghasilan_old
                            ? formatRupiah(Number(row.penghasilan_old))
                            : "-"}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {/* ... Button logic ... */}
                          {row.path_slip_gaji ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded-full"
                              onClick={() => {
                                setShowPdf(true);
                                setSelectedRow(row);
                              }}
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                          ) : (
                            <span className="text-slate-300 dark:text-slate-600 text-xs italic">
                              N/A
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {/* ... Actions ... */}
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                              onClick={() => {
                                setShowDetail((prev) =>
                                  prev && selectedRow?.nama === row.nama
                                    ? false
                                    : true,
                                );
                                setSelectedRow(row);
                              }}
                            >
                              <ChevronRight
                                className={`w-4 h-4 transition-transform duration-200 ${
                                  showDetail && selectedRow?.nama === row.nama
                                    ? "rotate-90"
                                    : ""
                                }`}
                              />
                            </Button>
                            {!isValidPermanen && (
                              <div>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8"
                                  onClick={() => {
                                    setShowEdit(true);
                                    setSelectedRow(row);
                                  }}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-red-600 dark:text-red-400"
                                  onClick={() => {
                                    setShowDelete(true);
                                    setSelectedRow(row);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-8 text-center text-slate-500 dark:text-slate-400"
                      >
                        <div className="flex flex-col items-center justify-center gap-2">
                          <Users className="w-8 h-8 text-slate-300 dark:text-slate-600" />
                          <p>Belum ada data keluarga yang ditambahkan.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* DETAIL ROW (Tetap di luar div overflow-x-auto) */}
            {showDetail && selectedRow && (
              <div className="bg-slate-50 dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 p-4 animate-in slide-in-from-top-2">
                {/* ... Isi Detail ... */}
                <h3 className="text-xs font-bold uppercase text-slate-500 mb-3 tracking-wider">
                  Detail {selectedRow.nama}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between border-b border-slate-200 dark:border-slate-800 pb-2">
                    <span className="text-slate-500">
                      Tempat, Tanggal Lahir
                    </span>
                    <span className="font-medium text-slate-900 dark:text-slate-200">
                      {selectedRow.tempat_lahir}, {selectedRow.tanggal_lahir}
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-slate-200 dark:border-slate-800 pb-2">
                    <span className="text-slate-500">Nomor Handphone</span>
                    <span className="font-medium text-slate-900 dark:text-slate-200">
                      {selectedRow.handphone}
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-slate-200 dark:border-slate-800 pb-2">
                    <span className="text-slate-500">Provinsi</span>
                    <span className="font-medium text-slate-900 dark:text-slate-200">
                      {selectedRow.ref_provinsi?.nama_wilayah}
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-slate-200 dark:border-slate-800 pb-2">
                    <span className="text-slate-500">Kabupaten</span>
                    <span className="font-medium text-slate-900 dark:text-slate-200">
                      {selectedRow.ref_kabupaten?.nama_wilayah}
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-slate-200 dark:border-slate-800 pb-2">
                    <span className="text-slate-500">Kecamatan</span>
                    <span className="font-medium text-slate-900 dark:text-slate-200">
                      {selectedRow.ref_kecamatan?.nama_wilayah}
                    </span>
                  </div>
                  <div className="flex gap-3 justify-between border-b border-slate-200 dark:border-slate-800 pb-2">
                    <span className="text-slate-500">Jalan</span>
                    <span className="text-end font-medium text-slate-900 dark:text-slate-200">
                      {selectedRow.jalan}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* --- SECTION 2: DATA TAMBAHAN --- */}
      <div className="space-y-4 pt-6 border-t border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-3 mb-4">
          <div
            className={`h-8 w-8 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
              isDataTambahanFilled
                ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"
                : "bg-slate-100 text-slate-500 dark:bg-slate-800"
            }`}
          >
            {isDataTambahanFilled ? <FaCheck /> : "2"}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
              Data Tambahan
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Lengkapi informasi mengenai jumlah tanggungan, biaya hidup
              keluarga per bulan, serta apakah terdapat anggota keluarga dalam
              Kartu Keluarga yang telah lulus Sarjana.
            </p>
          </div>
        </div>

        {isPendingDataKeluargaTambahan ? (
          <SkeletonRow />
        ) : (
          <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Form Inputs */}
              <div className="space-y-5">
                <div className="space-y-2">
                  <Label required>Biaya Hidup Keluarga / Bulan</Label>
                  <div className="relative">
                    <Wallet className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      required
                      placeholder="Contoh: 2.000.000"
                      className="pl-10"
                      value={
                        form.biaya_hidup_keluarga_perbulan
                          ? formatRupiah(
                              Number(form.biaya_hidup_keluarga_perbulan),
                            )
                          : ""
                      }
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          biaya_hidup_keluarga_perbulan: e.target.value.replace(
                            /\D/g,
                            "",
                          ),
                        }))
                      }
                      disabled={isValidPermanen}
                    />
                  </div>
                  <InputError
                    text={error?.response?.data.errors?.biaya_hidup_keluarga_perbulan?.join(
                      ",",
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label required>Jumlah Tanggungan Ortu/Wali</Label>
                  <Combobox
                    open={openJumlahTanggungan}
                    setOpen={setOpenJumlahTanggungan}
                    value={form.jumlah_tanggungan_orangtua_wali || ""}
                    setValue={(v) =>
                      setForm((prev) => ({
                        ...prev,
                        jumlah_tanggungan_orangtua_wali: v,
                      }))
                    }
                    options={dataJumlahTanggungan}
                    placeholder="Pilih Jumlah"
                  />
                  <InputError
                    text={error?.response?.data.errors?.jumlah_tanggungan_orangtua_wali?.join(
                      ",",
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label required>
                    Apakah dalam Kartu Keluarga Anda terdapat anggota keluarga
                    yang telah lulus pendidikan Sarjana?
                  </Label>
                  <Combobox
                    open={openSarjana}
                    setOpen={setOpenSarjana}
                    value={form.is_ada_sarjana_keluarga}
                    setValue={(v) => {
                      if (!v) return;
                      setForm((prev) => ({
                        ...prev,
                        is_ada_sarjana_keluarga: v,
                        ...(v === "0" ? { nama_sarjana_keluarga: "" } : {}),
                      }));
                    }}
                    options={[
                      { label: "Ada", value: "1" },
                      { label: "Tidak Ada", value: "0" },
                    ]}
                    placeholder="Pilih Ada/Tidak Ada"
                  />
                  <InputError
                    text={error?.response?.data.errors?.is_ada_sarjana_keluarga?.join(
                      ",",
                    )}
                  />
                </div>

                {String(form.is_ada_sarjana_keluarga) === "1" && (
                  <div className="space-y-2 animate-in fade-in zoom-in duration-300">
                    <Label required>Nama Anggota Keluarga yang Sarjana</Label>
                    <Input
                      required
                      placeholder="Masukkan nama anggota keluarga yang sarjana"
                      value={form.nama_sarjana_keluarga}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          nama_sarjana_keluarga: e.target.value,
                        }))
                      }
                      disabled={isValidPermanen}
                    />
                    <InputError
                      text={error?.response?.data.errors?.nama_sarjana_keluarga?.join(
                        ",",
                      )}
                    />
                  </div>
                )}
              </div>

              {/* Helper Info */}
              <div className="bg-blue-50 dark:bg-blue-900/10 p-4 rounded-lg border border-blue-100 dark:border-blue-900/30 text-sm text-slate-700 dark:text-slate-300">
                <div className="flex items-center gap-2 mb-3 text-blue-700 dark:text-blue-400 font-semibold">
                  <AlertCircle className="w-4 h-4" />
                  <span>Panduan Mengisi Tanggungan</span>
                </div>
                <p className="mb-2 leading-relaxed">
                  Jumlah tanggungan adalah anggota keluarga yang{" "}
                  <strong>tidak bekerja</strong> dan masih bergantung biayanya
                  pada orang tua/wali.
                </p>
                <div className="space-y-2 bg-white dark:bg-slate-950 p-3 rounded border border-blue-100 dark:border-blue-900/30 text-xs">
                  <p>
                    <strong>Contoh:</strong> Keluarga 6 orang, hanya Ayah yang
                    bekerja = <strong>5 Tanggungan</strong>.
                  </p>
                  <p>
                    <strong>Contoh:</strong> Keluarga 5 orang, Ayah & Kakak
                    sudah bekerja = <strong>3 Tanggungan</strong> (Ibu, Anda,
                    Adik).
                  </p>
                </div>
              </div>
            </div>

            {!isValidPermanen && (
              <div className="mt-6 flex justify-end">
                <Button
                  onClick={handleSumbitDataTambahan}
                  disabled={isPending}
                  className=""
                >
                  {isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FaCheck className="mr-2" />
                  )}
                  Simpan Data Tambahan
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* --- MODALS --- */}
      {/* Insert Modal */}
      <Modal
        show={showInsert}
        onClose={() => setShowInsert(false)}
        className="lg:w-[70vw] w-[95vw] max-h-[90vh] overflow-y-auto"
      >
        <Form
          method="POST"
          closeModal={() => setShowInsert(false)}
          idDataPendaftaran={idDataPendaftaran}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        show={showEdit}
        onClose={() => setShowEdit(false)}
        className="lg:w-[70vw] w-[95vw] max-h-[90vh] overflow-y-auto"
      >
        <Form
          method="PATCH"
          closeModal={() => {
            setShowEdit(false);
            setShowDetail(false);
          }}
          selectedRow={selectedRow}
        />
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDelete} onClose={() => setShowDelete(false)}>
        <Form
          method="DELETE"
          closeModal={() => setShowDelete(false)}
          selectedRow={selectedRow}
        />
      </Modal>

      {/* PDF Preview Modal */}
      <Modal
        show={showPdf}
        onClose={() => setShowPdf(false)}
        className="h-full md:w-[60vw] w-full"
      >
        <iframe
          src={`${selectedRow?.path_slip_gaji}`}
          title="Slip Gaji"
          className="absolute h-full w-full"
        />
      </Modal>
    </div>
  );
}
